import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { CompanyProfileTabProps } from "./OverviewTab";

const NONE = "-";

interface AddressRow extends DataTableRow {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  addressType: ReactNode;
}

/**
 * The Company Data tab's Addresses section — now a Carbon DataTable (street, city, state, postal
 * code, country, and Legal/Delivery badges) instead of a card list.
 */
const AddressesTab = ({ companyProfile }: CompanyProfileTabProps) => {
  const { t } = useTranslation();
  const addresses = companyProfile.addresses ?? [];

  const rows: AddressRow[] = addresses.map((address, index) => ({
    id: address.id ?? index,
    street: address.street || NONE,
    city: address.city || NONE,
    state: address.state || NONE,
    postalCode: address.postalCode || NONE,
    country: address.country?.name || NONE,
    addressType: (
      <div className="company-profile__badge-row">
        {address.isLegalAddress && (
          <Tag type="blue" size="sm">
            {t("companyProfile.legalAddressBadge")}
          </Tag>
        )}
        {address.isDeliverySite && (
          <Tag type="green" size="sm">
            {t("companyProfile.deliverySiteBadge")}
          </Tag>
        )}
      </div>
    ),
  }));

  const headers: DataTableColumn<AddressRow>[] = [
    { key: "street", header: t("companyProfile.table.street") },
    { key: "city", header: t("companyProfile.table.city") },
    { key: "state", header: t("companyProfile.table.state") },
    { key: "postalCode", header: t("companyProfile.table.postalCode") },
    { key: "country", header: t("companyProfile.table.country") },
    { key: "addressType", header: t("companyProfile.table.addressType") },
  ];

  return (
    <div className="company-profile__section">
      <h2 className="company-profile__section-heading">
        {t("companyProfile.addresses")}
      </h2>
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        empty={
          <EmptyState
            title={t("companyProfile.table.noAddresses")}
            description={t("companyProfile.table.noAddressesDesc")}
          />
        }
      />
    </div>
  );
};

export default AddressesTab;
